/**
 * useRiderInfoDetail Hook
 * 라이더 상세 정보 조회 및 수정 훅
 */
import { useState, useEffect, useCallback } from 'react';
import {
    RiderDetailInfo,
    RiderAccountInfo,
    RiderDeductionInfo,
    RiderApiItem,
    RiderType,
    BankOption
} from '../types/riderInfoDetail.types';
import { riderInfoDetailApi } from '../api/riderInfoDetailApi';
import { BANK_OPTIONS } from '../config/riderInfoDetail.config';
import {
    validateAccountInfo,
    sanitizeAccountNumber,
    sanitizeAccountHolder
} from '../lib/riderInfoDetailHelpers';
import { useToast } from '@/shared/components/feedback/Toast/ToastContext';
import { ToastType } from '@/shared/components/feedback/Toast/Toast.types';
import { getItem } from '@/shared/utils/storage';
import {
    formatDate,
    formatPhoneNumber,
    formatBirthDate,
    formatResidentNumber,
    formatContractStatus,
} from "@/shared/utils/formatter";
import { commonCodeApi } from '@/shared/api/commonCodeApi';

export const useRiderInfoDetail = (riderId?: string) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [bankOptions, setBankOptions] = useState<readonly BankOption[]>([{ value: '', label: '선택' }, ...BANK_OPTIONS.slice(1)]);
    const [riderInfo, setRiderInfo] = useState<RiderDetailInfo>({
        name: '',
        phone: '',
        id: riderId || '',
        birthDate: '',
        contractStatus: '',
        deliveryMethod: '',
        residentNumber: '',
        registrationDate: '',
        contractEndDate: ''
    });
    const [suppId, setSuppId] = useState<string>('');
    const [accountInfo, setAccountInfo] = useState<RiderAccountInfo>({
        bank: '',
        accountNumber: '',
        accountHolder: ''
    });
    const [deductions, setDeductions] = useState<RiderDeductionInfo[]>([]);
    const [riderTypes, setRiderTypes] = useState<RiderType[]>([]);
    const [selectedRiderType, setSelectedRiderType] = useState<string>('');
    const [initialRiderType, setInitialRiderType] = useState<string>('');
    const [riderTypeLoading, setRiderTypeLoading] = useState<boolean>(false);
    const { showToast } = useToast();
    const [userType, setUserType] = useState<string>('');

    // API 응답을 RiderDetailInfo로 변환
    const mapApiResponseToRiderInfo = useCallback(
        (apiData: RiderApiItem): RiderDetailInfo => {
            return {
                name: apiData.name || "",
                phone: formatPhoneNumber(apiData.phoneNumber || ""),
                id: apiData.riderId || "",
                birthDate: formatBirthDate(apiData.birthDate || ""),
                contractStatus: formatContractStatus(
                    apiData.status?.contractStatusName ||
                    apiData.contractStatusName ||
                    apiData.contStatus ||
                    "",
                ),
                deliveryMethod: apiData.deliveryMethName || "",
                residentNumber: formatResidentNumber(apiData.registNum || ""),
                registrationDate: formatDate(
                    apiData.riderInsDate || apiData.insDate || "",
                ),
                contractEndDate: formatDate(
                    apiData.contEdDate || apiData.updDate || "",
                ),
                supplierName: apiData.centerName || apiData.suppName || "",
                supplierId: apiData.suppId || "",
                riderTypeCode: apiData.riderType || apiData.typeCode || '',
                riderTypeName: apiData.riderTypeName || apiData.typeName || '',
                preAcctYn: apiData.preAcctYn || '',
                acctApplyDay: apiData.acctApplyDay || '',
                acctApplyType: apiData.acctApplyType || '',
                acctApplyTypeName: apiData.acctApplyTypeName || '',
                nextAcctApplyDay: apiData.nextAcctApplyDay || '',
                nextAcctApplyType: apiData.nextAcctApplyType || '',
                nextAcctApplyTypeName: apiData.nextAcctApplyTypeName || '',
            };
        },
        [],
    );

    // 일차감 정보 조회
    const fetchDeductions = useCallback(async (currentSuppId?: string) => {
        try {
            if (!riderId) return;

            const targetSuppId = currentSuppId || suppId;
            const deductionsData = await riderInfoDetailApi.getRiderDeductions(riderId, targetSuppId);

            if (deductionsData) {
                type DeductionRawItem = {
                    seqNo?: number;
                    deductSeqNo?: number;
                    deductName?: string;
                    deductType?: string;
                    calcType?: string;
                    deductAmt?: number;
                    startDay?: string;
                    endDay?: string;
                    startDate?: string;
                    endDate?: string;
                };

                let dataArray: DeductionRawItem[] = [];

                if (deductionsData && typeof deductionsData === 'object' && 'deductions' in deductionsData) {
                    dataArray = deductionsData.deductions || [];
                } else if (Array.isArray(deductionsData)) {
                    dataArray = deductionsData;
                } else if (typeof deductionsData === 'object') {
                    dataArray = [deductionsData as DeductionRawItem];
                }

                const mappedDeductions = dataArray.map((item: DeductionRawItem, index: number) => ({
                    id: item.seqNo || item.deductSeqNo || index + 1,
                    name: item.deductName || '',
                    type: item.deductType || '',
                    calcType: item.calcType || '1',
                    amount: item.deductAmt || 0,
                    startDate: formatBirthDate(item.startDay || item.startDate || ''),
                    endDate: formatBirthDate(item.endDay || item.endDate || '')
                }));

                setDeductions(mappedDeductions);
            }
        } catch {
            setDeductions([]);
        }
    }, [riderId, suppId]);

    // 라이더 상세 정보 조회
    const fetchRiderDetail = useCallback(async () => {
        if (!riderId) {
            setError('라이더 ID가 없습니다.');
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            const apiData = await riderInfoDetailApi.getRiderDetail(riderId);
            const mappedData = mapApiResponseToRiderInfo(apiData);
            setRiderInfo(mappedData);

            const currentSuppId = apiData.suppId || '';
            if (currentSuppId) {
                setSuppId(currentSuppId);
            }

            if (apiData.accountNum || apiData.bankCode) {
                setAccountInfo({
                    bank: apiData.bankCode || '',
                    accountNumber: apiData.accountNum || '',
                    accountHolder: apiData.accName || ''
                });
            }

            const riderTypeCode = apiData.riderType || apiData.typeCode || '';
            if (riderTypeCode) {
                const codeString = riderTypeCode.toString();
                setSelectedRiderType(codeString);
                setInitialRiderType(codeString);
            } else {
                setInitialRiderType('');
                setSelectedRiderType('');
            }

            if (currentSuppId) {
                await fetchDeductions(currentSuppId);
            }

        } catch (err: unknown) {
            const errorMessage = err instanceof Error ? err.message : '라이더 상세 정보를 불러오는데 실패했습니다.';
            setError(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [riderId, mapApiResponseToRiderInfo, fetchDeductions]);

    // 계좌 정보 저장
    const handleSaveAccount = useCallback(() => {
        const validationError = validateAccountInfo(
            accountInfo.bank,
            accountInfo.accountNumber,
            accountInfo.accountHolder
        );

        if (validationError) {
            showToast(validationError, { type: ToastType.WARNING });
            return;
        }

        const saveAccountInfo = async () => {
            try {
                if (!riderId) {
                    showToast('라이더 ID가 없습니다.', { type: ToastType.ERROR });
                    return;
                }

                const updateAccountData = {
                    riderId: riderId,
                    centerId: null,
                    accountNum: accountInfo.accountNumber,
                    accName: accountInfo.accountHolder,
                    bankCode: accountInfo.bank,
                    dpAcctNum: null,
                    dpBankCode: null,
                    dpAcctName: null
                };

                await riderInfoDetailApi.updateRiderAccount(riderId, updateAccountData);

                const updatedRiderData = await riderInfoDetailApi.getRiderDetail(riderId);
                if (updatedRiderData.accountNum || updatedRiderData.bankCode) {
                    setAccountInfo({
                        bank: updatedRiderData.bankCode || '',
                        accountNumber: updatedRiderData.accountNum || '',
                        accountHolder: updatedRiderData.accName || ''
                    });
                }

                showToast('계좌 정보가 성공적으로 저장되었습니다.', { type: ToastType.SUCCESS });
            } catch {
                showToast('계좌 정보 저장에 실패했습니다. 다시 시도해주세요.', { type: ToastType.ERROR });
            }
        };
        saveAccountInfo();
    }, [riderId, accountInfo, showToast]);

    useEffect(() => {
        fetchRiderDetail();
    }, [fetchRiderDetail]);

    useEffect(() => {
        const storedUserType = getItem<string>('USER_TYPE') || '';
        setUserType(storedUserType);
    }, []);

    const handleAccountNumberChange = useCallback((value: string) => {
        const sanitizedValue = sanitizeAccountNumber(value);
        setAccountInfo((prev) => ({ ...prev, accountNumber: sanitizedValue }));
    }, []);

    const handleAccountHolderChange = useCallback((value: string) => {
        const sanitizedValue = sanitizeAccountHolder(value);
        setAccountInfo((prev) => ({ ...prev, accountHolder: sanitizedValue }));
    }, []);

    const fetchRiderTypes = useCallback(async () => {
        try {
            setRiderTypeLoading(true);
            if (!riderId) {
                setRiderTypes([]);
                return;
            }
            const list = await riderInfoDetailApi.getRiderTypes(riderId);
            setRiderTypes(list);
        } catch {
            setRiderTypes([]);
        } finally {
            setRiderTypeLoading(false);
        }
    }, [riderId]);

    useEffect(() => {
        fetchRiderTypes();
    }, [fetchRiderTypes]);

    const handleRiderTypeChange = useCallback((value: string) => {
        setSelectedRiderType(value);
    }, []);

    const handleSaveRiderType = useCallback(async () => {
        if (!riderId) {
            showToast('라이더 ID가 없습니다.', { type: ToastType.ERROR });
            return;
        }
        if (!selectedRiderType) {
            showToast('라이더 유형을 선택해주세요.', { type: ToastType.WARNING });
            return;
        }

        if (selectedRiderType === initialRiderType) {
            showToast('변경된 라이더 유형이 없습니다.', { type: ToastType.WARNING });
            return;
        }

        try {
            await riderInfoDetailApi.updateRiderType({
                riderId,
                riderType: selectedRiderType,
            });
            showToast('라이더 유형이 저장되었습니다.', { type: ToastType.SUCCESS });
            setInitialRiderType(selectedRiderType);
        } catch {
            showToast('라이더 유형 저장에 실패했습니다. 다시 시도해주세요.', { type: ToastType.ERROR });
        }
    }, [riderId, selectedRiderType, initialRiderType, showToast]);

    const fetchBankOptions = useCallback(async () => {
        try {
            const response = await commonCodeApi.getCommonCodeList('BANK_CODE');

            if (response && response.codes && Array.isArray(response.codes)) {
                const options: BankOption[] = [
                    { value: '', label: '선택' },
                    ...response.codes
                        .filter(code => code.useYn === 'Y')
                        .map(code => ({
                            value: code.comCode,
                            label: code.comCdName
                        }))
                ];
                setBankOptions(options);
            }
        } catch {
            setBankOptions([{ value: '', label: '선택' }, ...BANK_OPTIONS.slice(1)]);
        }
    }, []);

    useEffect(() => {
        fetchBankOptions();
    }, [fetchBankOptions]);

    return {
        loading,
        error,
        riderInfo,
        suppId,
        accountInfo,
        setAccountInfo,
        deductions,
        bankOptions,
        riderTypes,
        riderTypeLoading,
        selectedRiderType,
        userType,
        fetchRiderDetail,
        fetchDeductions,
        handleSaveAccount,
        handleAccountNumberChange,
        handleAccountHolderChange,
        handleRiderTypeChange,
        handleSaveRiderType
    };
};
